import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import {
    Alert,
    AnomalyAlertResult,
    InboxAlert,
    QueryTrigger,
    SequenceTriggerAlertResult,
    TriggerType,
    UbaAlertResult
} from "@app2/clients/types/alert-typedefs";
import { queryStringForAlertResult } from "@app2/alert/alert-inbox/utils";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { SearchClientService } from "@app2/clients/search-client.service";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { CustomizeSearchColumnsModalComponent } from "@app2/search/search-shared/customize-search-columns-modal.component";
import { ImportantFieldData } from "@app2/search/search-shared/search-results-table.component";
import { ModalService } from "@app2/shared/modal/modal.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { TimeService } from "@app2/shared/services/time.service";
import { SavedQuery } from "@app2/type-defs/search/search-types";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { saveAs } from "file-saver";
import * as moment from "moment";
import { Observable } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { getMessageForError } from "@app2/util/errors/handleable-errors";

@UntilDestroy()
@Component({
    selector: "single-alert-view",
    template: `
        <ng-container *dsLoading="loadingAlert">
            <div *ngIf="trigger" class="single-alert-view-container">
                <div class="trigger-header-container">
                    <a class="link-text no-underline"
                       dsRouterLink="."
                       [queryParams]="{ mode: 'trigger', selected: trigger.id }"
                       queryParamsHandling="merge"
                       [matTooltip]="'GO_TO_TRIGGER' | localize"
                       queryParamsHandling="merge"
                       test-id="view-trigger">
                        <i class="far fa-arrow-left primary"></i>
                    </a>
                    <trigger-header [currentTrigger]="trigger"
                                    [isSticky]="false"></trigger-header>
                </div>
                <single-alert-view-header [alert]="alert"
                                          [trigger]="trigger"
                                          [primaryField]="primaryField"
                                          [queryString]="queryString"
                                          [previousAlertId]="newerAlertId"
                                          [nextAlertId]="olderAlertId">
                </single-alert-view-header>
                <important-fields-container *ngIf="trigger.type !== TriggerType.fraudUba"
                                            [importantFieldData]="importantFieldData"
                                            [resultTimestamp]="alert.resultTimestamp"
                                            [resultEndTimestamp]="alert.resultEndTimestamp"></important-fields-container>
                <uba-panels *ngIf="trigger.type === TriggerType.fraudUba" [riskFactors]="alert.riskFactors"></uba-panels>
                <div *ngIf="trigger.type !== TriggerType.sequence" test-id="recent-alert-events-container">
                    <h3 class="sub-header">
                        {{ "RECENT_ALERT_EVENTS" | localize }}
                        <div *ngIf="numEvents > 0" class="sub-header-action-container">
                            <ds-button type="blue-link"
                                       size="small"
                                       (clicked)="openCustomizeSearchColumnsModal()"
                                       test-id="custom-columns-button">
                                <i class="far fa-table-columns"></i>
                                {{ "MANAGE_COLUMNS" | localize }}
                            </ds-button>
                            <span class="separator-column">|</span>
                            <ds-button type="blue-link"
                                       size="small"
                                       [disabled]="downloadingEvents"
                                       (clicked)="downloadCsv()"
                                       test-id="download-csv-button">
                                <i class="far" [ngClass]="downloadingEvents ? 'fa-spinner fa-spin' : 'fa-cloud-download-alt'"></i>
                                {{ "GENERIC_DOWNLOAD_CSV" | localize }}
                            </ds-button>
                        </div>
                    </h3>
                    <events-table *dsLoading="!queryString"
                                  [queryString]="queryString"
                                  [begin]="begin"
                                  [end]="end"
                                  [filterList]="{ field: trigger.filterField, id: trigger.filterListId }"
                                  [excludeList]="{ field: trigger.excludeField, id: trigger.excludeListId }"
                                  [customColumns]="customColumns"
                                  (onEventsFetched)="numEvents = $event"
                                  (importantFieldDataChanged)="importantFieldData = $event"></events-table>
                    <div *ngIf="numEvents === 0" class="info-notification">
                        <i class="far fa-info-circle"></i>
                        <div class="info-text-container">
                            <span class="no-events-available-text">{{ 'INBOX_NO_ALERT_EVENTS_AVAILABLE' | localize }}</span>
                            <span class="expired-events-text">{{ 'INBOX_EXPIRED_EVENTS_FOR_ALERT' | localize }}</span>
                        </div>
                    </div>
                </div>
                <ng-container *ngIf="trigger.type === TriggerType.sequence">
                    <sequence-alert-result [trigger]="trigger"
                                           [customColumns]="customColumns"
                                           [alertResult]="alert"
                                           (importantFieldDataChanged)="importantFieldData = importantFieldData.concat($event)"></sequence-alert-result>
                </ng-container>
            </div>
        </ng-container>
    `,
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .single-alert-view-container {
            margin: 12px 16px;
        }

        .trigger-header-container {
            display: flex;
            align-items: baseline;
            gap: 8px;

            i.fa-arrow-left {
                font-size: 1.25rem;
            }

            trigger-header {
                width: 100%;
            }
        }

        .sub-header {
            margin: 16px 0;
        }

        events-table {
            width: 100%;
        }
    `],
})
export class SingleAlertViewComponent implements OnChanges {
    @Input() alertId: uuid;

    trigger: QueryTrigger;
    alert: InboxAlert | SequenceTriggerAlertResult | AnomalyAlertResult | UbaAlertResult;
    queryString: string;
    primaryField: string;
    savedQuery$: Observable<SavedQuery>;
    begin: Moment;
    end: Moment;
    numEvents: number;
    loadingAlert: boolean;
    importantFieldData: ImportantFieldData[] = [];
    downloadingEvents: boolean = false;
    customColumns: string[] = [];
    newerAlertId: uuid;
    olderAlertId: uuid;

    TriggerType = TriggerType;

    constructor(private alertService: AlertClientService,
                private searchClient: SearchClientService,
                private notificationsService: NotificationsService,
                private savedQueryService: SavedQueryService,
                private modalService: ModalService,
                private readonly timeService: TimeService) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.alertId && this.alertId) {
            this.loadingAlert = true;
            this.alertService.getAlertDetails(this.alertId)
                .then(alert => {
                    this.alert = alert;
                    this.begin = moment(this.alert.resultTimestamp);
                    this.end = this.alert.resultEndTimestamp
                        ? moment((this.alert as InboxAlert).resultEndTimestamp)
                        : moment((this.alert as InboxAlert).resultTimestamp).add(1, "days");

                    return this.alertService.getTrigger(this.alert.triggerId)
                        .then(trigger => {
                            this.trigger = trigger;
                            this.customColumns = trigger.customHeaders;
                            return this.setupAlertNavigation(alert, trigger);
                        })
                        .then((): Promise<SequenceTriggerAlertResult[] | AnomalyAlertResult[] | UbaAlertResult[] > => {
                            const filters = [`alertId:${ this.alertId }`, `triggerId:${ this.trigger.id }`,
                                `state:${ this.alert.currentState }`];

                            if (this.trigger.type !== TriggerType.sequence) {
                                this.getQueryInfo();
                            }

                            if (this.trigger.type === TriggerType.sequence) {
                                return this.alertService.getSequenceAlertPage(1, filters);
                            } else if (this.trigger.type === TriggerType.patternscoutSmartThreshold) {
                                return this.alertService.getAnomalyAlertPage(1, filters);
                            } else if (this.trigger.type === TriggerType.fraudUba) {
                                return this.alertService.getUbaAlertPage(1, filters);
                            }
                        })
                        .then(alertPage => {
                            if (alertPage) {
                                this.alert = {
                                    ...alertPage[0],
                                    id: alertPage[0].alertId,
                                };
                            }
                        })
                        .catch(error => this.handleAlertRetrievalFailure(error));
                })
                .catch(error => {
                    this.handleAlertRetrievalFailure(error);
                })
                .finally(() => this.loadingAlert = false);
        }

    }

    private handleAlertRetrievalFailure(error) {
        if (error instanceof HttpErrorResponse && error.status === 404) {
            this.notificationsService.showError("INBOX_ALERT_NOT_FOUND", error);
        } else {
            const errorMessage = getMessageForError(error.error, "ERROR_LOADING_ALERT");
            this.notificationsService.showError(errorMessage, error);
        }
    }

    private getQueryInfo(): void {
        this.savedQuery$ = this.trigger.savedQueryId
            ? this.savedQueryService.getSavedQuery$(this.trigger.savedQueryId)
                .pipe(shareReplay(1))
            : null;

        if (this.savedQuery$) {
            this.savedQuery$
                .pipe(untilDestroyed(this))
                .subscribe((savedQuery) => {
                    this.queryString = queryStringForAlertResult(this.trigger, savedQuery, this.alert as InboxAlert | AnomalyAlertResult | UbaAlertResult);
                    this.primaryField = savedQuery.primaryField;
                });
        } else if (this.trigger.type === TriggerType.fraudUba) {
            this.queryString = queryStringForAlertResult(this.trigger, null, this.alert as InboxAlert | AnomalyAlertResult | UbaAlertResult);
        } else {
            this.notificationsService.showError("TRIGGER_MISSING_SAVED_QUERY");
        }
    }

    downloadCsv() {
        const request = {
            queryString: this.queryString,
            begin: this.begin,
            end: this.end,
            pageSize: 50_000,
        };
        this.downloadingEvents = true;
        this.searchClient.downloadCsv(request)
            .then(blob => saveAs(blob, `DefenseStorm-AlertEvents-${ this.timeService.now().format(this.timeService.formats.timestampDate) }-${ this.trigger.name }.csv`))
            .catch(error => this.notificationsService.showError("CSV_DOWNLOAD_ERROR", error))
            .finally(() => this.downloadingEvents = false);
    }

    openCustomizeSearchColumnsModal(): void {
        this.modalService
            .open(CustomizeSearchColumnsModalComponent, {
                customColumns: this.customColumns,
            })
            .afterClosedWithResult()
            .subscribe(customColumns => this.customColumns = customColumns);
    }

    private setupAlertNavigation(alert: InboxAlert, trigger: QueryTrigger): Promise<unknown> {
        const boundary  = <Alert> {
            ...<any>alert,
            severity: trigger.severity,
            ticketSlug: null,
        };

        const navFilters = [`triggerId:${ this.trigger.id }`,
            `state:${ this.alert.currentState }`];

        const previousAlertPromise: Promise<string> = this.alertService.getAlertPage(false, "TIMESTAMP",
            trigger.domain, navFilters, boundary, 2).then(alerts => this.newerAlertId = alerts[0]?.id);
        const nextAlertPromise: Promise<string> = this.alertService.getAlertPage(true, "TIMESTAMP",
            trigger.domain, navFilters, boundary, 2).then(alerts => this.olderAlertId = alerts[0]?.id);

        return Promise.all([previousAlertPromise, nextAlertPromise])
            .catch(e => {
                const msg = getMessageForError(e.error, "ERROR_LOADING_ALERTS_FOR_NAVIGATION");
                this.notificationsService.showError(msg, e);
            });
    }
}
